/** Strip currency noise and parse a money input string to a number (signed, decimals). */
export const parseMoneyInput = (raw: string): number => {
    let cleaned = String(raw ?? '').replace(/[^0-9.-]/g, '');
    const neg = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    if (cleaned === '' || cleaned === '.') return 0;
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed)) return 0;
    return neg ? -parsed : parsed;
};

/** Keep a typed money string editable (optional leading -, one decimal point). */
export const sanitizeMoneyInputString = (raw: string): string => {
    const cleaned = String(raw ?? '').replace(/[^0-9.-]/g, '');
    const neg = cleaned.startsWith('-');
    const unsigned = cleaned.replace(/-/g, '');
    const dot = unsigned.indexOf('.');
    const normalized = dot < 0
        ? unsigned
        : unsigned.slice(0, dot + 1) + unsigned.slice(dot + 1).replace(/\./g, '');
    return (neg ? '-' : '') + normalized;
};

/** Format a number for display in money inputs (comma thousands separators). */
export const formatMoneyInput = (
    value: number,
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number; emptyIfZero?: boolean }
): string => {
    if (options?.emptyIfZero && (!value || value === 0)) return '';
    const minimumFractionDigits = options?.minimumFractionDigits ?? 2;
    const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
    return Number(value || 0).toLocaleString('en-US', { minimumFractionDigits, maximumFractionDigits });
};

/** Display money with a leading $ and 2 decimal places (verification / detail surfaces). */
export const formatMoneyDisplay = (value: number | null | undefined): string =>
    `$${formatMoneyInput(Number(value || 0))}`;

/** Whole-dollar money for wage list columns. */
export const formatMoneyWholeDisplay = (value: number | null | undefined): string =>
    `$${Math.round(Number(value || 0)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
